import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Paper, Stack, Chip, TextField, InputAdornment, MenuItem, Tabs, Tab, Accordion, AccordionSummary,
  AccordionDetails, Table, TableHead, TableRow, TableCell, TableBody, Button, IconButton, Tooltip, Alert, LinearProgress,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ApiIcon from '@mui/icons-material/Api';
import Paged from '../components/Paged';
import { API_DOCS_QUERY } from '../graphql/queries';

const ACCESS_COLOR = (a) => (a === 'Admins' ? 'error' : a === 'Public' ? 'success' : a.startsWith('Permission') ? 'warning' : 'default');
const endpoint = () => `${window.location.origin}/graphql`;

function Code({ children, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Box sx={{ position: 'relative', mb: 1.5 }}>
      {label && <Typography variant="caption" fontWeight={700} color="text.secondary">{label}</Typography>}
      <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, pr: 5, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1, fontSize: 12.5, overflowX: 'auto', whiteSpace: 'pre' }}>
        {children}
      </Box>
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton size="small" onClick={copy} sx={{ position: 'absolute', top: label ? 24 : 4, right: 4, color: 'grey.300' }}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const curlFor = (op) => [
  `curl -X POST ${endpoint()} \\`,
  `  -H "Content-Type: application/json" \\`,
  `  -H "Authorization: Bearer <token>" \\`,
  `  -d '${JSON.stringify({ query: op.example.query, variables: op.example.variables })}'`,
].join('\n');

// The whole reference as Markdown (for sharing or offline reading)
function toMarkdown(docs) {
  const out = [`# Matrimony API reference`, '', `Generated ${new Date(docs.generatedAt).toLocaleString()} from the live schema.`, '',
    `- Endpoint: \`POST ${endpoint()}\` (GraphQL)`, '- Authentication: `login` returns a token; send it as `Authorization: Bearer <token>`',
    '- Admins and matchmakers act for a family with `X-Family-User-Id: <family user id>`; admins act for a vendor with `X-Vendor-User-Id: <vendor user id>`',
    '- Dates are ISO strings; amounts are numbers in rupees', '',
    `${docs.stats.queries} queries · ${docs.stats.mutations} mutations · ${docs.stats.types} types · ${docs.stats.inputs} inputs`, ''];
  for (const area of docs.areas) {
    out.push(`## ${area.name}`, '');
    for (const op of [...area.queries, ...area.mutations]) {
      out.push(`### \`${op.name}\` (${op.kind})`, '', op.summary, '', `Who can call it: **${op.access}** · Returns: \`${op.type}\``, '');
      if (op.args.length) {
        out.push('| Argument | Type | Required |', '|---|---|---|', ...op.args.map(a => `| ${a.name} | \`${a.type}\` | ${a.required ? 'yes' : 'no'} |`), '');
      }
      out.push('```graphql', op.example.query, '```', '');
    }
  }
  out.push('## Types', '');
  for (const t of docs.types) {
    out.push(`### ${t.name} (${t.kind})`, '', ...(t.description ? [t.description, ''] : []));
    if (t.values) out.push(`Values: ${t.values.join(', ')}`, '');
    if (t.fields.length) out.push('| Field | Type | Notes |', '|---|---|---|', ...t.fields.map(f => `| ${f.name} | \`${f.type}\` | ${(f.description || '').replace(/\|/g, '/')} |`), '');
  }
  return out.join('\n');
}

function Operation({ op, onType }) {
  return (
    <Accordion disableGutters variant="outlined" TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' }, mb: 0.75 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1, minWidth: 0 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={op.kind === 'query' ? 'Query' : 'Mutation'} color={op.kind === 'query' ? 'info' : 'secondary'} sx={{ width: 76 }} />
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{op.name}</Typography>
          <Chip size="small" variant="outlined" color={ACCESS_COLOR(op.access)} label={op.access} />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }} noWrap>{op.summary}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" mb={1.5}>{op.summary}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={700}>Returns</Typography>
          <Chip size="small" label={op.type} sx={{ fontFamily: 'monospace' }} onClick={() => onType(op.baseType)} clickable />
          <Typography variant="body2" fontWeight={700} sx={{ ml: 2 }}>Who can call it</Typography>
          <Chip size="small" color={ACCESS_COLOR(op.access)} label={op.access} />
        </Stack>
        {op.args.length > 0 && (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}><TableCell>Argument</TableCell><TableCell>Type</TableCell><TableCell>Required</TableCell><TableCell>Default</TableCell></TableRow></TableHead>
            <TableBody>
              {op.args.map(a => (
                <TableRow key={a.name}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.name}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={a.type} sx={{ fontFamily: 'monospace' }} onClick={() => onType(a.baseType)} clickable /></TableCell>
                  <TableCell>{a.required ? 'yes' : 'no'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.defaultValue || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Code label="Request">{op.example.query}</Code>
        {op.args.length > 0 && <Code label="Variables">{JSON.stringify(op.example.variables, null, 2)}</Code>}
        <Code label="cURL">{curlFor(op)}</Code>
      </AccordionDetails>
    </Accordion>
  );
}

function TypeCard({ t, onType, open }) {
  return (
    <Accordion disableGutters variant="outlined" defaultExpanded={open} TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' }, mb: 0.75 }} id={`type-${t.name}`}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip size="small" label={t.kind} sx={{ width: 64 }} color={t.kind === 'input' ? 'secondary' : 'default'} />
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.name}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{t.description || `${t.fields.length} field(s)`}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {t.description && <Typography variant="body2" mb={1}>{t.description}</Typography>}
        {t.values && <Typography variant="body2">Values: <code>{t.values.join(', ')}</code></Typography>}
        {t.fields.length > 0 && (
          <Table size="small">
            <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}><TableCell>Field</TableCell><TableCell>Type</TableCell><TableCell>Notes</TableCell></TableRow></TableHead>
            <TableBody>
              {t.fields.map(f => (
                <TableRow key={f.name}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{f.name}{f.args.length ? `(${f.args.map(a => a.name).join(', ')})` : ''}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={f.type} sx={{ fontFamily: 'monospace' }} onClick={() => onType(f.baseType)} clickable /></TableCell>
                  <TableCell><Typography variant="caption">{f.description || ''}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// Admin > API Docs: the API reference, generated from the live schema
export default function AdminApiDocsPage() {
  const { data, loading, error } = useQuery(API_DOCS_QUERY, { fetchPolicy: 'cache-first' });
  const docs = data?.apiDocs;
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [area, setArea] = useState('');
  const [kind, setKind] = useState('all');
  const [access, setAccess] = useState('');
  const [typeSearch, setTypeSearch] = useState('');
  const [openType, setOpenType] = useState(null);

  const q = search.trim().toLowerCase();
  const areas = useMemo(() => (docs?.areas || []).map(a => ({
    ...a,
    ops: [...a.queries, ...a.mutations].filter(op => (kind === 'all' || op.kind === kind)
      && (!access || (access === 'Permission' ? op.access.startsWith('Permission') : op.access === access))
      && (!q || [op.name, op.summary, op.type, ...op.args.map(x => x.name)].some(t => (t || '').toLowerCase().includes(q)))),
  })).filter(a => (!area || a.name === area) && a.ops.length), [docs, kind, access, q, area]);
  const shownOps = areas.reduce((n, a) => n + a.ops.length, 0);

  const tq = typeSearch.trim().toLowerCase();
  const types = (docs?.types || []).filter(t => !tq || t.name.toLowerCase().includes(tq) || t.fields.some(f => f.name.toLowerCase().includes(tq)));
  const showType = (name) => {
    if (!docs?.types.some(t => t.name === name)) return;
    setTypeSearch(name); setOpenType(name); setTab(2);
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([toMarkdown(docs)], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'matrimony-api.md'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={2} spacing={1}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <ApiIcon color="primary" />
            <Typography variant="h4" fontWeight={700}>API Docs</Typography>
            <Chip size="small" color="error" variant="outlined" label="Admins only" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Every query and mutation of the application's API, generated from the live schema, with who may call it and an example request.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={download} disabled={!docs}>Download (Markdown)</Button>
      </Stack>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error">{error.message}</Alert>}

      {docs && (
        <>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
            <Chip label={`${docs.stats.queries} queries`} color="info" variant="outlined" />
            <Chip label={`${docs.stats.mutations} mutations`} color="secondary" variant="outlined" />
            <Chip label={`${docs.stats.types} types`} variant="outlined" />
            <Chip label={`${docs.stats.inputs} inputs`} variant="outlined" />
            <Chip label={`${docs.areas.length} areas`} variant="outlined" />
          </Stack>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Getting started" />
            <Tab label="Operations" />
            <Tab label="Types" />
          </Tabs>

          {tab === 0 && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Endpoint</Typography>
              <Typography variant="body2" paragraph>
                One GraphQL endpoint for everything: send a <code>POST</code> with a JSON body <code>{'{ "query": "...", "variables": { ... } }'}</code>.
              </Typography>
              <Code>{`POST ${endpoint()}`}</Code>

              <Typography variant="h6" fontWeight={700} gutterBottom mt={2}>Authentication</Typography>
              <Typography variant="body2" paragraph>Call <code>login</code> with an email and password; it returns a token (valid for 7 days by default). Send it on every request:</Typography>
              <Code label="1. Get a token">{`curl -X POST ${endpoint()} -H "Content-Type: application/json" \\\n  -d '{"query":"mutation { login(email: \\"admin@matrimony.com\\", password: \\"<password>\\") }"}'`}</Code>
              <Code label="2. Use it">{`Authorization: Bearer <token>`}</Code>

              <Typography variant="h6" fontWeight={700} gutterBottom mt={2}>Acting for a family or a vendor</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}><TableCell>Header</TableCell><TableCell>Who</TableCell><TableCell>Effect</TableCell></TableRow></TableHead>
                <TableBody>
                  <TableRow><TableCell sx={{ fontFamily: 'monospace' }}>X-Family-User-Id: &lt;user id&gt;</TableCell><TableCell>Admins; matchmakers for their own clients</TableCell><TableCell>"my…" queries and mutations work on that bride/groom family's data</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontFamily: 'monospace' }}>X-Vendor-User-Id: &lt;user id&gt;</TableCell><TableCell>Admins</TableCell><TableCell>Vendor service and offering operations work for that vendor</TableCell></TableRow>
                </TableBody>
              </Table>

              <Typography variant="h6" fontWeight={700} gutterBottom>Who can call an operation</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                <Chip size="small" color="success" label="Public" /><Typography variant="body2">no sign-in needed</Typography>
                <Chip size="small" label="Signed-in users" /><Typography variant="body2">any signed-in account (data is limited to the caller's own)</Typography>
                <Chip size="small" color="warning" label="Permission: …" /><Typography variant="body2">the caller's role or plan needs that permission</Typography>
                <Chip size="small" color="error" label="Admins" /><Typography variant="body2">system administrators only</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">Read from each operation's code when this page is generated.</Typography>

              <Typography variant="h6" fontWeight={700} gutterBottom mt={2}>Conventions</Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 0 }}>
                <li>Dates and times are ISO strings (e.g. <code>2026-09-30T07:43:48.331Z</code>); date-only fields are <code>YYYY-MM-DD</code>.</li>
                <li>Amounts are numbers in rupees; <code>JSON</code> fields carry structured data (e.g. offering details, dashboards).</li>
                <li>Errors come back in the GraphQL <code>errors</code> array with a readable <code>message</code>; HTTP status is 200.</li>
                <li>The API schema (introspection) and this page are available to admins only.</li>
              </Typography>
            </Paper>
          )}

          {tab === 1 && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                  <TextField size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search operations (name, description, argument)"
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                  <TextField select size="small" label="Area" value={area} onChange={e => setArea(e.target.value)} sx={{ minWidth: 240 }}>
                    <MenuItem value="">All areas</MenuItem>
                    {docs.areas.map(a => <MenuItem key={a.name} value={a.name}>{a.name} ({a.queries.length + a.mutations.length})</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Who can call it" value={access} onChange={e => setAccess(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">Anyone</MenuItem>
                    {['Public', 'Signed-in users', 'Permission', 'Admins'].map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </TextField>
                  <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v) => v && setKind(v)}>
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="query">Queries</ToggleButton>
                    <ToggleButton value="mutation">Mutations</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Typography variant="caption" color="text.secondary">{shownOps} operation(s)</Typography>
              </Paper>
              {areas.map(a => (
                <Box key={a.name} mb={3}>
                  <Typography variant="h6" fontWeight={700} mb={1}>{a.name} <Typography component="span" variant="body2" color="text.secondary">({a.ops.length})</Typography></Typography>
                  <Paged items={a.ops}>{rows => rows.map(op => <Operation key={op.kind + op.name} op={op} onType={showType} />)}</Paged>
                </Box>
              ))}
              {!areas.length && <Alert severity="info">No operations match.</Alert>}
            </>
          )}

          {tab === 2 && (
            <>
              <TextField size="small" fullWidth sx={{ mb: 2 }} value={typeSearch} onChange={e => { setTypeSearch(e.target.value); setOpenType(null); }} placeholder="Search types and fields"
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <Divider sx={{ mb: 2 }} />
              <Paged items={types}>{rows => rows.map(t => <TypeCard key={t.name + (openType === t.name)} t={t} onType={showType} open={openType === t.name} />)}</Paged>
              {!types.length && <Alert severity="info">No types match.</Alert>}
            </>
          )}
        </>
      )}
    </Box>
  );
}
